import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useParsersOverview } from "./hooks/useParsersOverview";
import KPICard from "./components/KPICard";
import SourceStatusBadge from "./components/SourceStatusBadge";
import LinksLineChart from "./components/LinksLineChart";
import { NodeOverviewRow } from "../../api/ParsersPanel";

const ALL_NODES_OPTION = "__all__";

type SortKey = keyof Pick<
  NodeOverviewRow,
  "label" | "count_last_7d" | "count_prev_7d" | "count_3mo" | "total" | "drop_pct" | "last_collection_at"
>;

const OverviewTab: React.FC = () => {
  const [selectedNode, setSelectedNode] = useState<string>(ALL_NODES_OPTION);
  const [expandedNode, setExpandedNode] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortAsc, setSortAsc] = useState(false);

  const nodeFilter = selectedNode === ALL_NODES_OPTION ? undefined : selectedNode;
  const { rows: rawRows, loading, error } = useParsersOverview(nodeFilter, 7);

  const rows = useMemo(() => {
    if (!sortKey) return rawRows;
    return [...rawRows].sort((a, b) => {
      const av = a[sortKey] ?? (sortKey === "drop_pct" ? -Infinity : "");
      const bv = b[sortKey] ?? (sortKey === "drop_pct" ? -Infinity : "");
      if (av < bv) return sortAsc ? -1 : 1;
      if (av > bv) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [rawRows, sortKey, sortAsc]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc((a) => !a);
    else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const sortIndicator = (key: SortKey) => (sortKey === key ? (sortAsc ? " ▲" : " ▼") : "");

  const totalActive = rows.filter((r) => r.status === "ok").length;
  const totalSilent = rows.filter((r) => r.status === "silent").length;
  const totalDropping = rows.filter((r) => r.status === "dropping").length;

  const nodeOptions = rawRows.map((r) => r.node);

  return (
    <Container>
      <TopRow>
        <KPICards>
          <KPICard label="Total Nodes" value={rows.length} />
          <KPICard label="Healthy" value={totalActive} accentHealthy />
          <KPICard label="Dropping" value={totalDropping} accent />
          <KPICard label="Silent" value={totalSilent} accentSilent/>
        </KPICards>

        <NodeFilterWrap>
          <FilterLabel>Filter by node</FilterLabel>
          <Select
            value={selectedNode}
            onChange={(e) => {
              setSelectedNode(e.target.value);
              setExpandedNode(null);
            }}
          >
            <option value={ALL_NODES_OPTION}>All nodes</option>
            {nodeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </NodeFilterWrap>
      </TopRow>

      {loading && <Msg>Loading…</Msg>}
      {error && <ErrorMsg>{error}</ErrorMsg>}

      {!loading && !error && (
        <>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th onClick={() => toggleSort("label")}>Node{sortIndicator("label")}</Th>
                  <Th align="right" onClick={() => toggleSort("count_last_7d")}>
                    Last 7d{sortIndicator("count_last_7d")}
                  </Th>
                  <Th align="right" onClick={() => toggleSort("count_prev_7d")}>
                    Prev 7d{sortIndicator("count_prev_7d")}
                  </Th>
                  <Th align="right" onClick={() => toggleSort("count_3mo")}>
                    3 Months{sortIndicator("count_3mo")}
                  </Th>
                  <Th align="right" onClick={() => toggleSort("total")}>
                    Total{sortIndicator("total")}
                  </Th>
                  <Th align="right" title="% below (or above, if negative) the median of the trailing 6 weeks, not a simple week-over-week comparison." onClick={() => toggleSort("drop_pct")}>
                    Difference %{sortIndicator("drop_pct")}
                  </Th>
                  <Th onClick={() => toggleSort("last_collection_at")}>
                    Last Collected{sortIndicator("last_collection_at")}
                  </Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <React.Fragment key={row.node}>
                    <Tr
                      $clickable
                      onClick={() =>
                        setExpandedNode(expandedNode === row.node ? null : row.node)
                      }
                    >
                      <Td>
                        <NodeName>{row.label}</NodeName>
                        <NodeKey>{row.node}</NodeKey>
                      </Td>
                      <Td align="right">{row.count_last_7d.toLocaleString()}</Td>
                      <Td align="right">{row.count_prev_7d.toLocaleString()}</Td>
                      <Td align="right">{row.count_3mo.toLocaleString()}</Td>
                      <Td align="right">{row.total.toLocaleString()}</Td>
                      <Td align="right">
                        {row.drop_pct !== null ? (
                          <DropPct $dropping={row.drop_pct > 0}>
                            {row.drop_pct > 0 ? "▼" : "▲"} {Math.abs(row.drop_pct)}%
                          </DropPct>
                        ) : (
                          "-"
                        )}
                      </Td>
                      <Td>{row.last_collection_at ?? "-"}</Td>
                      <Td>
                        <SourceStatusBadge status={row.status} />
                      </Td>
                    </Tr>
                    {expandedNode === row.node && row.daily_series.length > 0 && (
                      <Tr>
                        <Td colSpan={8} style={{ padding: "12px 20px 20px" }}>
                          <LinksLineChart
                            data={row.daily_series}
                            height={160}
                            label={`${row.label} - last 30 days`}
                          />
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </TableWrapper>

          <CardList>
            {rows.map((row) => {
              const open = expandedNode === row.node;
              return (
                <Card key={row.node}>
                  <CardHeader onClick={() => setExpandedNode(open ? null : row.node)}>
                    <div>
                      <NodeName>{row.label}</NodeName>
                      <NodeKey>{row.node}</NodeKey>
                    </div>
                    <SourceStatusBadge status={row.status} />
                  </CardHeader>
                  <CardGrid>
                    <CardCell>
                      <CellLabel>Last 7d</CellLabel>
                      <CellValue>{row.count_last_7d.toLocaleString()}</CellValue>
                    </CardCell>
                    <CardCell>
                      <CellLabel>Prev 7d</CellLabel>
                      <CellValue>{row.count_prev_7d.toLocaleString()}</CellValue>
                    </CardCell>
                    <CardCell>
                      <CellLabel>3 Months</CellLabel>
                      <CellValue>{row.count_3mo.toLocaleString()}</CellValue>
                    </CardCell>
                    <CardCell>
                      <CellLabel>Total</CellLabel>
                      <CellValue>{row.total.toLocaleString()}</CellValue>
                    </CardCell>
                    <CardCell>
                      <CellLabel title="% below (or above, if negative) the median of the trailing 6 weeks, not a simple week-over-week comparison.">Difference %</CellLabel>
                      <CellValue>
                        {row.drop_pct !== null ? (
                          <DropPct $dropping={row.drop_pct > 0}>
                            {row.drop_pct > 0 ? "▼" : "▲"} {Math.abs(row.drop_pct)}%
                          </DropPct>
                        ) : (
                          "-"
                        )}
                      </CellValue>
                    </CardCell>
                    <CardCell>
                      <CellLabel>Last Collected</CellLabel>
                      <CellValue>{row.last_collection_at ?? "-"}</CellValue>
                    </CardCell>
                  </CardGrid>
                  {open && row.daily_series.length > 0 && (
                    <ChartWrap>
                      <LinksLineChart
                        data={row.daily_series}
                        height={160}
                        label={`${row.label} - last 30 days`}
                      />
                    </ChartWrap>
                  )}
                </Card>
              );
            })}
          </CardList>
        </>
      )}
    </Container>
  );
};

export default OverviewTab;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const KPICards = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    width: 100%;
  }
`;

const NodeFilterWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const FilterLabel = styled.label`
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
`;

const Select = styled.select`
  padding: 7px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  background: #fff;
  min-width: 200px;
  cursor: pointer;

  @media (max-width: 480px) {
    width: 100%;
    min-width: 0;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);

  @media (max-width: 768px) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  min-width: 800px;
`;

const Th = styled.th<{ align?: string }>`
  background: #0058A9;
  color: #fff;
  text-align: ${({ align }) => align ?? "left"};
  padding: 10px 14px;
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  &:hover {
    background: #004a90;
  }
`;

const Tr = styled.tr<{ $clickable?: boolean }>`
  border-bottom: 1px solid #f0f0f0;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  &:hover {
    background: ${({ $clickable }) => ($clickable ? "#f6f9ff" : "transparent")};
  }
`;

const Td = styled.td<{ align?: string }>`
  padding: 10px 14px;
  text-align: ${({ align }) => align ?? "left"};
  vertical-align: middle;
`;

const CardList = styled.div`
  display: none;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
  padding: 14px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 10px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px 14px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const CardCell = styled.div`
  display: flex;
  flex-direction: column;
`;

const CellLabel = styled.span`
  font-size: 10px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const CellValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #222;
  word-break: break-word;
`;

const ChartWrap = styled.div`
  margin-top: 14px;
`;

const NodeName = styled.p`
  font-weight: 600;
  color: #222;
  margin: 0;
  font-size: 13px;
`;

const NodeKey = styled.p`
  font-size: 10px;
  color: #aaa;
  margin: 2px 0 0;
`;

const DropPct = styled.span<{ $dropping: boolean }>`
  font-weight: 700;
  color: ${({ $dropping }) => ($dropping ? "#e65100" : "#2e7d32")};
`;

const Msg = styled.p`
  color: #aaa;
  font-size: 14px;
`;

const ErrorMsg = styled.p`
  color: #c62828;
  font-size: 14px;
`;
